import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../services/axios';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import { getTodayDateInputValue } from '../utils/date';

function GenerarPlanillaColectora() {
    const { API_URL } = useAuth();
    const navigate = useNavigate();
    const [zonas, setZonas] = useState([]);
    const [zonaSeleccionada, setZonaSeleccionada] = useState('');
    const [generando, setGenerando] = useState(false);
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        type: 'info',
        title: '',
        message: '',
        onConfirm: null
    });

    useEffect(() => {
        cargarZonas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cargarZonas = async () => {
        try {
            const response = await axios.get(`${API_URL}/zonas`);
            setZonas(response.data);
        } catch (error) {
            console.error('Error al cargar zonas:', error);
            setModalConfig({
                isOpen: true,
                type: 'error',
                title: 'Error',
                message: 'No se pudieron cargar las zonas',
                onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
            });
        }
    };

    const generarPlanilla = async () => {
        if (!zonaSeleccionada) {
            setModalConfig({
                isOpen: true,
                type: 'warning',
                title: 'Zona Requerida',
                message: 'Por favor seleccione una zona para generar la planilla',
                onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
            });
            return;
        }

        setGenerando(true);
        try {
            const response = await axios.get(
                `${API_URL}/planilla-colectora/zona/${zonaSeleccionada}`,
                {
                    responseType: 'blob', // Importante para recibir el PDF
                }
            );

            // Crear un blob y descargar el archivo
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const fecha = getTodayDateInputValue();
            const zonaNombre = zonas.find(z => z.id === parseInt(zonaSeleccionada))?.nombre || 'zona';
            link.setAttribute('download', `planilla_colectora_${zonaNombre}_${fecha}.pdf`);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setModalConfig({
                isOpen: true,
                type: 'success',
                title: '¡Planilla Generada!',
                message: `La planilla colectora para la zona ${zonaNombre} se ha descargado correctamente.\n\nFecha: ${new Date().toLocaleString('es-AR')}`,
                onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
            });
        } catch (error) {
            console.error('Error al generar planilla:', error);
            setModalConfig({
                isOpen: true,
                type: 'error',
                title: 'Error al Generar Planilla',
                message: error.response?.data?.message || error.message || 'Ocurrió un error al generar la planilla',
                onConfirm: () => setModalConfig({ ...modalConfig, isOpen: false })
            });
        } finally {
            setGenerando(false);
        }
    };

    return (
        <div className="flex h-screen">
            <Sidebar />

            <main className="flex-1 overflow-y-auto bg-background-light dark:bg-background-dark">
                <div className="p-8">
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-6">
                        <button
                            onClick={() => navigate('/operario/dashboard')}
                            className="flex items-center justify-center h-10 w-10 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <span className="material-symbols-outlined">arrow_back</span>
                        </button>
                        <div>
                            <h1 className="text-[#111618] dark:text-white text-4xl font-black leading-tight tracking-[-0.033em]">
                                Generar Planilla Colectora
                            </h1>
                            <p className="text-[#617c89] dark:text-gray-400 text-sm mt-1">
                                Planilla para registro manual de lecturas por zona
                            </p>
                        </div>
                    </div>

                    {/* Card principal */}
                    <div className="bg-white dark:bg-background-dark dark:border dark:border-gray-700 rounded-xl shadow-sm p-6 max-w-2xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-primary/10 dark:bg-primary/20 rounded-lg p-3">
                                <span className="material-symbols-outlined text-primary text-3xl">description</span>
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-[#111618] dark:text-white">
                                    Configuración de Planilla
                                </h2>
                                <p className="text-sm text-[#617c89] dark:text-gray-400">
                                    Seleccione la zona para generar el PDF
                                </p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-[#111618] dark:text-white mb-2">
                                    Zona *
                                </label>
                                <select
                                    value={zonaSeleccionada}
                                    onChange={(e) => setZonaSeleccionada(e.target.value)}
                                    className="w-full h-12 px-4 rounded-lg bg-[#f0f3f4] dark:bg-gray-700 border-none text-[#111618] dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                                >
                                    <option value="">Seleccionar zona...</option>
                                    {zonas.map((zona) => (
                                        <option key={zona.id} value={zona.id}>
                                            {zona.nombre} (Código: {zona.valor})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Información del formato */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">info</span>
                                    <div className="flex-1">
                                        <p className="font-medium text-blue-800 dark:text-blue-200 text-sm mb-2">
                                            La planilla incluye:
                                        </p>
                                        <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1 list-disc list-inside">
                                            <li>Orden, Padrón, Nombre y Dirección de cada cliente</li>
                                            <li>Última lectura en formato PIN (6 casillas)</li>
                                            <li>6 casillas vacías para registrar nueva lectura manualmente</li>
                                            <li>Fecha y hora de generación</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Botón generar */}
                            <button
                                onClick={generarPlanilla}
                                disabled={generando || !zonaSeleccionada}
                                className="w-full flex items-center justify-center gap-2 h-12 px-6 bg-primary text-white rounded-lg font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {generando ? (
                                    <>
                                        <span className="material-symbols-outlined animate-spin">refresh</span>
                                        Generando Planilla...
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined">download</span>
                                        Generar y Descargar Planilla
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            {/* Modal */}
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
                type={modalConfig.type}
                title={modalConfig.title}
                message={modalConfig.message}
                onConfirm={modalConfig.onConfirm}
            />
        </div>
    );
}

export default GenerarPlanillaColectora;
